import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// Customer billing summary (2026-10-22): "Summary of all Customer" — International / Local /
// Total per customer, filtered by centre and location; on demand (screen + Excel) and scheduled e-mail.

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16, color: '#1e293b' };
const sel: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', color: '#1e293b' };
const btn = (primary?: boolean): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 6, border: primary ? 'none' : '1px solid #cbd5e1', background: primary ? '#2563eb' : '#fff', color: primary ? '#fff' : '#1e293b', cursor: 'pointer', fontSize: 13, fontWeight: 600 });
const th: React.CSSProperties = { border: '1px solid #cbd5e1', padding: '4px 6px', fontSize: 12, textAlign: 'center', background: '#f1f5f9', fontWeight: 600 };
const td: React.CSSProperties = { border: '1px solid #e2e8f0', padding: '3px 6px', fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap' };
const DOW = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hms = (s: number) => { s = Math.round(s || 0); return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`; };

function presetRange(p: string, cycleDay: number): [string, string] | null {
  const now = new Date();
  if (p === 'this_month') return [ymd(new Date(now.getFullYear(), now.getMonth(), 1)), ymd(now)];
  if (p === 'last_month') return [ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)), ymd(new Date(now.getFullYear(), now.getMonth(), 0))];
  if (p === 'last_cycle') {
    const cd = Math.min(28, Math.max(1, cycleDay || 1));
    // the last complete cycle: cycle day → day before the next cycle day
    const endMonth = now.getDate() >= cd ? now.getMonth() : now.getMonth() - 1;
    const end = new Date(now.getFullYear(), endMonth, cd - 1);
    const start = new Date(now.getFullYear(), endMonth - 1, cd);
    return [ymd(start), ymd(end)];
  }
  return null;
}

export default function CustomerSummaryReport({ siteId: siteCtx = '' }: any) {
  const [opts, setOpts] = useState<any>({ sites: [], locations: [] });
  const [siteId, setSiteId] = useState(siteCtx); // starts on the "Managing site" selection
  const [locId, setLocId] = useState('');
  useEffect(() => { setSiteId(siteCtx); setLocId(''); }, [siteCtx]);
  const [preset, setPreset] = useState('last_cycle');
  const [cycleDay, setCycleDay] = useState(() => { try { return Number(localStorage.getItem('ce_rep_cycle')) || 16; } catch { return 16; } });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [incTec, setIncTec] = useState(false);
  const [rep, setRep] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { axios.get('/api/billing/customer-summary/options').then((r) => setOpts(r.data)).catch((e) => setMsg(e?.response?.data?.error || 'Could not load centres')); }, []);
  useEffect(() => {
    const r = presetRange(preset, cycleDay);
    if (r) { setFrom(r[0]); setTo(r[1]); }
    try { localStorage.setItem('ce_rep_cycle', String(cycleDay)); } catch { /* ignore */ }
  }, [preset, cycleDay]);

  const locs = useMemo(() => opts.locations.filter((l: any) => !siteId || String(l.site_id) === siteId), [opts, siteId]);
  const params = () => ({ from, to, site_id: siteId || undefined, location_id: locId || undefined, currency, include_tec: incTec ? 1 : 0 });

  const run = async () => {
    setBusy(true); setMsg('');
    try { setRep((await axios.get('/api/billing/customer-summary', { params: params() })).data); }
    catch (e: any) { setMsg(e?.response?.data?.error || 'Could not run the report'); }
    setBusy(false);
  };
  const download = async (kind: 'xlsx' | 'csv') => {
    setBusy(true); setMsg('');
    try {
      const r = await axios.get(`/api/billing/customer-summary/${kind}`, { params: params(), responseType: 'blob' });
      const name = (String(r.headers['content-disposition'] || '').match(/filename="([^"]+)"/) || [])[1] || `Customer-Summary.${kind}`;
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e: any) { setMsg('Could not build the file'); }
    setBusy(false);
  };

  const blank3 = <><td style={td} /><td style={td} /><td style={td} /></>;
  const repMoney = (v: number) => `${rep?.currency === 'USD' ? '$' : 'AED '}${(v || 0).toFixed(2)}`;

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Customer billing summary</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          International, local and total calls for every customer. Choose a centre and location, run it on screen or download it as Excel — or schedule it below to be e-mailed automatically.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ fontSize: 12 }}>Centre<br />
            <select style={sel} value={siteId} onChange={(e) => { setSiteId(e.target.value); setLocId(''); }}>
              <option value="">All centres</option>
              {opts.sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></label>
          <label style={{ fontSize: 12 }}>Location<br />
            <select style={sel} value={locId} onChange={(e) => setLocId(e.target.value)}>
              <option value="">All locations</option>
              {locs.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></label>
          <label style={{ fontSize: 12 }}>Period<br />
            <select style={sel} value={preset} onChange={(e) => setPreset(e.target.value)}>
              <option value="last_cycle">Last billing cycle</option>
              <option value="last_month">Last month</option>
              <option value="this_month">This month so far</option>
              <option value="custom">Custom dates</option>
            </select></label>
          {preset === 'last_cycle' && (
            <label style={{ fontSize: 12 }}>Cycle starts on day<br />
              <input type="number" min={1} max={28} style={{ ...sel, width: 70 }} value={cycleDay} onChange={(e) => setCycleDay(Number(e.target.value) || 1)} /></label>
          )}
          <label style={{ fontSize: 12 }}>From<br /><input type="date" style={sel} value={from} onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }} /></label>
          <label style={{ fontSize: 12 }}>To<br /><input type="date" style={sel} value={to} onChange={(e) => { setTo(e.target.value); setPreset('custom'); }} /></label>
          <label style={{ fontSize: 12 }}>Currency<br />
            <select style={sel} value={currency} onChange={(e) => setCurrency(e.target.value)}><option>AED</option><option>USD</option></select></label>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
            <input type="checkbox" checked={incTec} onChange={(e) => setIncTec(e.target.checked)} /> Include TEC's own users</label>
          <button style={btn(true)} disabled={busy} onClick={run}>{busy ? 'Working…' : 'Run report'}</button>
          <button style={btn()} disabled={busy} onClick={() => download('xlsx')}>Download Excel</button>
          <button style={btn()} disabled={busy} onClick={() => download('csv')}>Download CSV</button>
        </div>
        {msg && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{msg}</div>}
      </div>

      {rep && (
        <div style={card}>
          <div style={{ fontSize: 18, marginBottom: 2 }}>Summary of all Customer</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
            Since {rep.period.from} 00:00:00 Until {rep.period.to} 23:59:59 · Centre: {rep.labels.centre} · Location: {rep.labels.location} · {rep.rows.length} customers
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr><th style={th} rowSpan={3}>Customer</th><th style={th} rowSpan={3}>Customer Code</th><th style={th} rowSpan={3}>Period</th>
                  <th style={th} colSpan={6}>Summary</th><th style={th} colSpan={3} rowSpan={2}>Total</th></tr>
                <tr><th style={th} colSpan={3}>International</th><th style={th} colSpan={3}>Local</th></tr>
                <tr>{[0, 1, 2].map((i) => <React.Fragment key={i}><th style={th}>Duration</th><th style={th}>Charge</th><th style={th}>Calls</th></React.Fragment>)}</tr>
              </thead>
              <tbody>
                {rep.rows.map((r: any, i: number) => (
                  <tr key={i}>
                    <td style={td}>{r.name}</td><td style={td}>{r.code}</td><td style={td}>{rep.period.from} 00:00:00 - {rep.period.to} 23:59:59</td>
                    {r.intl.calls ? <><td style={td}>{hms(r.intl.sec)}</td><td style={td}>{repMoney(r.intl.charge)}</td><td style={td}>{r.intl.calls}</td></> : blank3}
                    {r.total.calls ? <><td style={td}>{hms(r.local.sec)}</td><td style={td}>{repMoney(r.local.charge)}</td><td style={td}>{r.local.calls}</td></> : blank3}
                    <td style={td}>{hms(r.total.sec)}</td><td style={td}>{repMoney(r.total.charge)}</td><td style={td}>{r.total.calls}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td style={td}>TOTAL (screen only)</td><td style={td}>{rep.rows.length} customers</td><td style={td} />
                  <td style={td}>{hms(rep.total.intl.sec)}</td><td style={td}>{repMoney(rep.total.intl.charge)}</td><td style={td}>{rep.total.intl.calls}</td>
                  <td style={td}>{hms(rep.total.local.sec)}</td><td style={td}>{repMoney(rep.total.local.charge)}</td><td style={td}>{rep.total.local.calls}</td>
                  <td style={td}>{hms(rep.total.total.sec)}</td><td style={td}>{repMoney(rep.total.total.charge)}</td><td style={td}>{rep.total.total.calls}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {rep.converted && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Amounts recorded in {rep.currency === 'AED' ? 'USD' : 'AED'} are shown in {rep.currency} at the fixed rate 1 USD = {rep.peg} AED.</div>}
        </div>
      )}

      <Schedules opts={opts} />
    </div>
  );
}

function Schedules({ opts }: { opts: any }) {
  const [list, setList] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const load = () => axios.get('/api/billing/report-schedules').then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const blank = { name: 'Monthly customer billing summary', frequency: 'monthly', cycle_day: 16, day_of_week: 1, run_hour: 8, site_id: '', location_id: '', currency: 'AED', include_tec: false, recipients: '', enabled: true };
  const save = async () => {
    setMsg('');
    try {
      if (edit.id) await axios.put(`/api/billing/report-schedules/${edit.id}`, edit); else await axios.post('/api/billing/report-schedules', edit);
      setEdit(null); load();
    } catch (e: any) { setMsg(e?.response?.data?.error || 'Could not save'); }
  };
  const toggle = async (s: any) => { await axios.put(`/api/billing/report-schedules/${s.id}`, { ...s, enabled: !s.enabled }).catch(() => {}); load(); };
  const del = async (s: any) => { if (!window.confirm(`Delete the schedule "${s.name}"?`)) return; await axios.delete(`/api/billing/report-schedules/${s.id}`).catch(() => {}); load(); };
  const runNow = async (s: any) => {
    setNote(`Sending "${s.name}"…`);
    try { const r = await axios.post(`/api/billing/report-schedules/${s.id}/run`); setNote(`Sent "${s.name}" for ${r.data.period.from} to ${r.data.period.to} to ${s.recipients}`); }
    catch (e: any) { setNote(`Not sent: ${e?.response?.data?.error || 'error'}`); }
    load();
  };
  const when = (s: any) => s.frequency === 'daily' ? `Daily at ${pad(s.run_hour)}:00 — previous day`
    : s.frequency === 'weekly' ? `Every ${DOW[s.day_of_week]} at ${pad(s.run_hour)}:00 — previous 7 days`
    : `Monthly on day ${s.cycle_day} at ${pad(s.run_hour)}:00 — cycle day ${s.cycle_day} to day ${s.cycle_day === 1 ? 'end of month' : s.cycle_day - 1}`;
  const locs = opts.locations.filter((l: any) => !edit || !edit.site_id || String(l.site_id) === String(edit.site_id));
  const set = (k: string, v: any) => setEdit((x: any) => ({ ...x, [k]: v }));

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div><div style={{ fontSize: 16, fontWeight: 700 }}>Scheduled reports</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>E-mailed automatically as Excel (times are Dubai time). Each period is sent once.</div></div>
        <button style={btn(true)} onClick={() => { setMsg(''); setEdit({ ...blank }); }}>+ New schedule</button>
      </div>
      {note && <div style={{ fontSize: 12, color: note.startsWith('Not sent') ? '#dc2626' : '#16a34a', marginBottom: 8 }}>{note}</div>}
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr>{['Name', 'When', 'Centre / location', 'Send to', 'Last sent', ''].map((h) => <th key={h} style={{ ...th, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>
          {list.map((s) => (
            <tr key={s.id} style={{ opacity: s.enabled ? 1 : 0.55 }}>
              <td style={{ ...td, textAlign: 'left' }}><b>{s.name}</b>{!s.enabled && ' (paused)'}<div style={{ color: '#64748b' }}>{s.currency}{s.include_tec ? ' · incl. TEC users' : ''}</div></td>
              <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal' }}>{when(s)}</td>
              <td style={{ ...td, textAlign: 'left' }}>{s.site_name || 'All centres'} / {s.location_name || 'All locations'}</td>
              <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal', maxWidth: 220 }}>{s.recipients}</td>
              <td style={{ ...td, textAlign: 'left' }}>{s.last_run ? <span style={{ color: s.last_run.status === 'sent' ? '#16a34a' : '#dc2626' }}>{s.last_run.status === 'sent' ? 'Sent' : 'Failed'} {String(s.last_run.created_at).slice(0, 16).replace('T', ' ')}<br /><span style={{ color: '#64748b' }}>{s.last_run.period_from?.slice(0, 10)} → {s.last_run.period_to?.slice(0, 10)}</span></span> : '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>
                <button style={{ ...btn(), padding: '3px 8px', fontSize: 12 }} onClick={() => runNow(s)}>Send now</button>{' '}
                <button style={{ ...btn(), padding: '3px 8px', fontSize: 12 }} onClick={() => { setMsg(''); setEdit({ ...s, site_id: s.site_id || '', location_id: s.location_id || '', include_tec: !!s.include_tec, enabled: !!s.enabled }); }}>Edit</button>{' '}
                <button style={{ ...btn(), padding: '3px 8px', fontSize: 12 }} onClick={() => toggle(s)}>{s.enabled ? 'Pause' : 'Resume'}</button>{' '}
                <button style={{ ...btn(), padding: '3px 8px', fontSize: 12, color: '#dc2626' }} onClick={() => del(s)}>Delete</button>
              </td>
            </tr>
          ))}
          {!list.length && <tr><td style={{ ...td, textAlign: 'left', color: '#64748b' }} colSpan={6}>No scheduled reports yet.</td></tr>}
        </tbody>
      </table>

      {edit && (
        <div style={{ marginTop: 14, padding: 14, border: '1px solid #cbd5e1', borderRadius: 8, background: '#f8fafc' }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{edit.id ? 'Edit schedule' : 'New schedule'}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12 }}>Name<br /><input style={{ ...sel, width: 260 }} value={edit.name} onChange={(e) => set('name', e.target.value)} /></label>
            <label style={{ fontSize: 12 }}>How often<br />
              <select style={sel} value={edit.frequency} onChange={(e) => set('frequency', e.target.value)}>
                <option value="monthly">Monthly (billing cycle)</option><option value="weekly">Weekly</option><option value="daily">Daily</option>
              </select></label>
            {edit.frequency === 'monthly' && <label style={{ fontSize: 12 }}>Cycle starts / sent on day<br /><input type="number" min={1} max={28} style={{ ...sel, width: 80 }} value={edit.cycle_day} onChange={(e) => set('cycle_day', Number(e.target.value) || 1)} /></label>}
            {edit.frequency === 'weekly' && <label style={{ fontSize: 12 }}>Day<br /><select style={sel} value={edit.day_of_week} onChange={(e) => set('day_of_week', Number(e.target.value))}>{DOW.slice(1).map((d, i) => <option key={d} value={i + 1}>{d}</option>)}</select></label>}
            <label style={{ fontSize: 12 }}>Time<br /><select style={sel} value={edit.run_hour} onChange={(e) => set('run_hour', Number(e.target.value))}>{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{pad(h)}:00</option>)}</select></label>
            <label style={{ fontSize: 12 }}>Centre<br /><select style={sel} value={edit.site_id} onChange={(e) => { set('site_id', e.target.value); set('location_id', ''); }}><option value="">All centres</option>{opts.sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
            <label style={{ fontSize: 12 }}>Location<br /><select style={sel} value={edit.location_id} onChange={(e) => set('location_id', e.target.value)}><option value="">All locations</option>{locs.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></label>
            <label style={{ fontSize: 12 }}>Currency<br /><select style={sel} value={edit.currency} onChange={(e) => set('currency', e.target.value)}><option>AED</option><option>USD</option></select></label>
            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 6 }}><input type="checkbox" checked={!!edit.include_tec} onChange={(e) => set('include_tec', e.target.checked)} /> Include TEC's own users</label>
          </div>
          <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Send to (e-mail addresses, separated by commas)<br />
            <input style={{ ...sel, width: '100%', boxSizing: 'border-box' }} value={edit.recipients} placeholder="billing@example.com, finance@example.com" onChange={(e) => set('recipients', e.target.value)} /></label>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
            {edit.frequency === 'monthly' ? `Sent on day ${edit.cycle_day} of each month, covering day ${edit.cycle_day} of the previous month up to the day before.`
              : edit.frequency === 'weekly' ? 'Covers the 7 days before the day it is sent.' : 'Covers the previous day.'}
          </div>
          {msg && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{msg}</div>}
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button style={btn(true)} onClick={save}>Save schedule</button>
            <button style={btn()} onClick={() => setEdit(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
